import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import api from "api/axiosConfig";
import API_BASE from "configs/hostConfig";
import { Formik, Form, Field, ErrorMessage } from 'formik';
import {
  Box,
  Flex,
  Heading,
  Button,
  Container,
  FormControl,
  Input,
  Text,
  Textarea,
  VStack,
  useToast,
  useBreakpointValue,
} from '@chakra-ui/react';
import { TbAlertCircle } from 'react-icons/tb';
import PromptCard from './components/PromptCard';
import theme from 'ui/theme/theme';
import InactiveBanner from './components/InactiveBanner';
import { essay_schema } from './data/essay_schema';

import * as Yup from 'yup';

function DraftingForm({
  breadcrumbProg,
  setBreadcrumbProg,
  nextPath,
  returnUrl,
  isPlanActive,
}) {
  // console.log('----------------entering brain storm page');

  const navigate = useNavigate();
  const { essayId } = useParams();
  const [currDraft, setCurrDraft] = useState('');
  const [title, setTitle] = useState('');
  const [promptData, setPromptData] = useState({});
  const [enableNext, setEnableNext] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const containerWidth = useBreakpointValue({
    base: '100%',
    md: '90%',
    lg: '80%',
  });

  const essayDetails = {
    ...essay_schema,
    ...JSON.parse(localStorage.getItem('essayExerciseDetails')),
  };

  // Validation schema using Yup
  const validationSchema = Yup.object({
    title: Yup.string().required('Title is required'),
    draft: Yup.string().required('Draft is required'),
  });

  const handleWordCount = (text) => {
    const count = text?.trim() ? text.trim().split(/\s+/).length : 0;
    setWordCount(count);
  };

  function updateStateData(details) {
    setTitle(details.draftTitle || details.title);
    setCurrDraft(details.draft);
    setPromptData({
      essayTitle: details.essayTitle,
      essayType: details.secondaryType || details.primaryType,
      essayPrompt: details.prompt,
      url: details.illustration,
      level: details.level,
      minWordCount: details.minWordCount,
    });
    setEnableNext(details.progress > 3 ? true : false);
    handleWordCount(details.draft);
  }

  useEffect(() => {
    setBreadcrumbProg(breadcrumbProg);
  }, [setBreadcrumbProg, breadcrumbProg]);

  useEffect(() => {
    if (essayDetails?.essayId == essayId) {
      updateStateData(essayDetails);
    }
    if (
      essayDetails?.essayId != essayId ||
      ((essayDetails.progress > 2 ||
        (essayDetails.progress === 2 && essayDetails.status != 'To Do')) &&
        !essayDetails.draft)
    ) {
      api
        .get(`${API_BASE}/api/essay-exercise/retrieve-data?exerciseId=${essayId}`)
        .then((res) => {
          if (res.status == 200) {
            const newExerciseDetails = res.data?.data;
            updateStateData(newExerciseDetails);
            localStorage.setItem(
              'essayExerciseDetails',
              JSON.stringify(newExerciseDetails)
            );
          }
        })
        .catch(() =>
          console.log(
            'failed to fetch essay details at draft page, id: ',
            essayId
          )
        );
    }
  }, []);

  function updateDraft(values, finished) {
    // console.log('to update brainstorm ideas: ', values);
    if ((!values?.draft || values.draft.length < 20) && finished) {
      const warning =
        'Your submission is a bit brief. We know you have great ideas to share!';
      toastMessage(warning);
      return;
    }
    setIsSubmitting(true);
    api
      .patch(`${API_BASE}/api/essay-exercise/draft`, { ...values, finished: finished })
      .then((res) => {
        // console.log('======= updateDraft response: ', res);
        if (res.status == 200) {
          localStorage.setItem(
            'essayExerciseDetails',
            JSON.stringify(res.data?.data)
          );
          if (finished) {
            navigate(nextPath);
          }
        }
      })
      .catch(() => toastMessage('Failed to update draft.'))
      .finally(() => setIsSubmitting(false));
  }

  function toastMessage(msg) {
    toast({
      position: 'top',
      render: () => (
        <Box
          border="1px solid"
          // color={theme.colors.semantics['warning-light']}
          borderColor={theme.colors.semantics.warning}
          p={4}
          bg={theme.colors.semantics['warning-light']}
          borderRadius={12}
          wordBreak="break-all"
        >
          <Flex>
            <TbAlertCircle color={theme.colors.semantics.warning} />
            <Flex ml="12px" flex="1">
              <Text fontWeight="bold" ml="2">
                {msg}
              </Text>
            </Flex>
          </Flex>
        </Box>
      ),
      duration: 3000,
    });
  }

  return (
    <Container width={containerWidth} maxW="none">
      {!isPlanActive && <InactiveBanner />}
      <Flex flexDirection="column" marginBottom="16px">
        <PromptCard promptData={promptData} defaultShow={false} />
        <Box
          overflowY="auto" // Independent scroll for this section
          p={4}
          borderRight="1px solid #e0e0e0"
          bg="gray.50"
          minW="360px"
        >
          <Formik
            initialValues={{
              title: title,
              draft: currDraft,
              exerciseId: essayId,
            }}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={() => { }}
          >
            {({ values, handleChange, isValid }) => (
              <Form>
                <VStack spacing={2} align="stretch">
                  {/* Title Input */}
                  <Heading size="md" color="teal.700">
                    Drafting
                  </Heading>
                  <Box bg="white">
                    <FormControl isInvalid={!isValid && !values.title}>
                      <Field
                        as={Input}
                        id="title"
                        name="title"
                        placeholder="Enter the title"
                        size="lg"
                        fontWeight="bold"
                      />
                      <ErrorMessage
                        name="title"
                        component={Text}
                        color="red.500"
                      />
                    </FormControl>

                    {/* Draft Textarea */}
                    <FormControl isInvalid={!isValid && !values.draft}>
                      <Field
                        as={Textarea}
                        id="draft"
                        name="draft"
                        placeholder="Enter your draft"
                        size="lg"
                        height="calc(100vh - 265px)"
                        value={values.draft}
                        onChange={(e) => {
                          handleChange(e); // Update Formik state
                          handleWordCount(e.target.value); // Update word count
                        }}
                      />
                      <ErrorMessage
                        name="draft"
                        component={Text}
                        color="red.500"
                      />
                    </FormControl>
                  </Box>
                  <Box mt={1}>
                    <Flex align="center" justify="space-between" w="100%">
                      <Text color="gray.500">Word Count: {wordCount}</Text>
                      <Flex gap={2} mr={4}>
                        <Button
                          colorScheme="gray"
                          type="button"
                          variant="link"
                          onClick={() => navigate(returnUrl)}
                        >
                          Back
                        </Button>
                        {enableNext ? (
                          <Button
                            colorScheme="green"
                            type="button"
                            onClick={() => navigate(nextPath)}
                          >
                            Next
                          </Button>
                        ) : (
                          <>
                            {/* Update Draft Button */}
                            <Button
                              colorScheme="green"
                              variant="outline"
                              onClick={() => updateDraft(values, false)}
                              isDisabled={!isValid || isSubmitting}
                              isLoading={isSubmitting}
                              type="button"
                            >
                              Save for later
                            </Button>

                            {/* Submit Draft Button */}
                            <Button
                              colorScheme="green"
                              onClick={() => updateDraft(values, true)}
                              isDisabled={!isValid || isSubmitting}
                              isLoading={isSubmitting}
                              type="button"
                            >
                              Submit
                            </Button>
                          </>
                        )}
                      </Flex>
                    </Flex>
                  </Box>
                </VStack>
              </Form>
            )}
          </Formik>
        </Box>
      </Flex>
    </Container>
  );
}

DraftingForm.propTypes = {
  breadcrumbProg: PropTypes.number,
  setBreadcrumbProg: PropTypes.func,
  nextPath: PropTypes.string,
  returnUrl: PropTypes.string,
  isPlanActive: PropTypes.bool,
};

export default DraftingForm;
